import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Wind, Mountain, AlertTriangle } from 'lucide-react';
import './GuidePage.css';

export default function FahrtechnikGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🚴 Fahrtechnik für Schottland-Touren</h1>
        
        <div className="info-box">
          <p>
            Schottland bietet Touren für jedes Level - von sanften Lowlands bis zu brutalen 
            Highland-Pässen. Mit der richtigen Technik wird's einfacher!
          </p>
        </div>

        <section>
          <h2>1. Anstiege meistern</h2>
          
          <h3><TrendingUp size={24} /> Die Basics</h3>
          <ul>
            <li><strong>Gang rechtzeitig runterschalten:</strong> Bevor der Anstieg zu steil wird</li>
            <li><strong>Gleichmäßiges Tempo:</strong> Nicht zu schnell starten</li>
            <li><strong>Sitzposition:</strong> Aufrecht bleiben, Gewicht nach vorne</li>
            <li><strong>Atmung:</strong> Tief & rhythmisch atmen</li>
          </ul>

          <h3>Lange Anstiege (Highlands!)</h3>
          <div className="pro-tip">
            <h4>💡 Die "Scottish Climb" Strategie</h4>
            <p>
              Highland-Pässe können 5-10km lang sein mit 8-12% Steigung. 
              <strong>Pace dich!</strong>
            </p>
            <ul>
              <li><strong>60-70 RPM (Trittfrequenz):</strong> Langsamer Rhythmus, weniger Kraft</li>
              <li><strong>Alle 5-10 Min:</strong> Kurz aufstehen & Muskeln lockern</li>
              <li><strong>Mental:</strong> In Abschnitte teilen - "bis zur nächsten Kurve"</li>
              <li><strong>Pausen:</strong> Lieber 3x 2 Min. als einmal 10 Min.</li>
            </ul>
          </div>

          <h3>Steile Rampen (15%+)</h3>
          <ul>
            <li><strong>Gewicht nach vorne:</strong> Sonst hebt das Vorderrad ab</li>
            <li><strong>Im Sattel bleiben:</strong> Bei losem Untergrund (Traktion!)</li>
            <li><strong>Pendeln erlaubt:</strong> Spart Kraft bei sehr steilen Stellen</li>
            <li><strong>Absteigen OK:</strong> Kein Schande! Schieben ist besser als umkippen</li>
          </ul>

          <h3>E-Bike Anstiege</h3>
          <div className="info-box">
            <h4>⚡ E-Bike Anstiegs-Tipps</h4>
            <ul>
              <li><strong>Eco/Tour-Modus:</strong> Reicht oft, spart Akku</li>
              <li><strong>Turbo nur punktuell:</strong> Für kurze, steile Rampen</li>
              <li><strong>Trittfrequenz hoch:</strong> 70-80 RPM optimal für Motor</li>
              <li><strong>Reichweite im Auge behalten:</strong> Anstiege fressen Akku!</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>2. Abfahrten sicher bewältigen</h2>
          
          <h3><TrendingDown size={24} /> Grundposition</h3>
          <ul>
            <li><strong>Arme leicht gebeugt:</strong> Federung durch Körper</li>
            <li><strong>Gewicht nach hinten:</strong> Hintern hinter Sattel (bei steilen Abfahrten)</li>
            <li><strong>Blick nach vorne:</strong> 5-10m vorausschauen</li>
            <li><strong>Knie als Stoßdämpfer:</strong> Leicht gebeugt</li>
          </ul>

          <h3>Bremsen richtig einsetzen</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Vorsicht bei Nässe!</strong> Schottische Straßen sind oft nass. 
            Bremsweg verdoppelt sich!
          </div>

          <ul>
            <li><strong>Beide Bremsen:</strong> 60% vorne, 40% hinten (bei Trockenheit)</li>
            <li><strong>Bei Nässe:</strong> Mehr hinten, weniger vorne (Sturzgefahr!)</li>
            <li><strong>Modulieren:</strong> Nicht blockieren lassen</li>
            <li><strong>Vor Kurven:</strong> Bremsen beenden, dann lenken</li>
          </ul>

          <h3>Kurventechnik</h3>
          <ol>
            <li><strong>VOR der Kurve:</strong> Geschwindigkeit reduzieren</li>
            <li><strong>Blick durch die Kurve:</strong> Dahin schauen, wo du hin willst</li>
            <li><strong>Äußeres Pedal unten:</strong> Inneres Pedal oben (kein Bodenkontakt!)</li>
            <li><strong>Gewicht auf äußeres Pedal:</strong> Gibt Traktion</li>
            <li><strong>Lenken mit Körper:</strong> Rad "unter dir durchschieben"</li>
          </ol>

          <h3>Single-Track Roads abwärts</h3>
          <p>
            Enge Straßen, oft mit Schlaglöchern & Gegenverkehr:
          </p>
          <ul>
            <li><strong>Kontrollierte Geschwindigkeit:</strong> Nicht zu schnell!</li>
            <li><strong>Passing Places nutzen:</strong> Bei Gegenverkehr anhalten</li>
            <li><strong>Schafe!</strong> Können plötzlich auf Straße laufen</li>
            <li><strong>Cattle Grids:</strong> Langsam & gerade überqueren</li>
          </ul>
        </section>

        <section>
          <h2>3. Gegenwind & Seitenwind</h2>
          
          <h3><Wind size={24} /> Wind in Schottland</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Schottischer Wind ist brutal!</strong> Besonders in Highlands & an Küsten. 
            30-40 km/h Wind ist normal, bei Stürmen bis 80 km/h möglich!
          </div>

          <h3>Gegenwind-Strategie</h3>
          <ul>
            <li><strong>Kleinere Gänge:</strong> Hohe Trittfrequenz (80-90 RPM)</li>
            <li><strong>Aerodynamik:</strong> Flacher auf dem Rad (Oberlenker greifen)</li>
            <li><strong>Gruppe:</strong> Windschatten fahren (wechseln!)</li>
            <li><strong>Mental:</strong> Akzeptieren, dass es langsam geht</li>
            <li><strong>Pausen:</strong> Windgeschützte Ecken suchen</li>
          </ul>

          <h3>Seitenwind (GEFÄHRLICH!)</h3>
          <ul>
            <li><strong>Stabile Position:</strong> Unterlenker greifen, Gewicht zentral</li>
            <li><strong>Böen erwarten:</strong> Besonders nach Gebäuden/Bäumen</li>
            <li><strong>Gegenlenken:</strong> Leicht in den Wind lenken (Übung nötig!)</li>
            <li><strong>Lenkradtaschen:</strong> Windanfällig - bei Sturm abmontieren</li>
            <li><strong>Absteigen bei Sturm:</strong> Keine Schande, Sicherheit geht vor!</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Planung bei Wind</h3>
            <p>
              Wetter-App checken! In Schottland kann Wind die Richtung ändern. 
              <strong>Morning Glory:</strong> Morgens oft weniger Wind, nachmittags stärker. 
              Schwere Etappen morgens planen!
            </p>
          </div>
        </section>

        <section>
          <h2>4. Gruppenfahren</h2>
          
          <h3>Windschatten-Fahren</h3>
          <ul>
            <li><strong>Abstand:</strong> 30-50cm zum Vordermann (Übung nötig!)</li>
            <li><strong>Rotieren:</strong> Alle 2-5 Min. wechseln (vorne fährt sich schwerer)</li>
            <li><strong>Kommunikation:</strong> Handzeichen für Hindernisse, Richtungswechsel</li>
            <li><strong>Gleichmäßiges Tempo:</strong> Nicht beschleunigen, wenn du vorne bist</li>
          </ul>

          <h3>Handzeichen</h3>
          <div className="checklist">
            <div className="check-item">
              <strong>Schlagloch links/rechts:</strong> Mit Hand zeigen & rufen
            </div>
            <div className="check-item">
              <strong>Auto von hinten:</strong> Rufen "Auto!" oder Hand heben
            </div>
            <div className="check-item">
              <strong>Anhalten:</strong> Hand hoch & "Stopp!" rufen
            </div>
            <div className="check-item">
              <strong>Rechts/Links abbiegen:</strong> Arm ausstrecken
            </div>
          </div>

          <h3>Sicherheit in der Gruppe</h3>
          <ul>
            <li><strong>Nie überholen auf Abfahrten</strong> (zu gefährlich!)</li>
            <li><strong>Anstiege:</strong> Jeder im eigenen Tempo, oben warten</li>
            <li><strong>Single-Track:</strong> Einzeln fahren, Autos vorbeilassen</li>
            <li><strong>Regelmäßige Stops:</strong> Alle 20-30km kurz warten</li>
          </ul>
        </section>

        <section>
          <h2>5. Schaltung optimal nutzen</h2>
          
          <h3>Wann schalten?</h3>
          <ul>
            <li><strong>VOR dem Anstieg:</strong> Nicht unter Last schalten (Kettenverschleiß!)</li>
            <li><strong>Trittfrequenz 70-90 RPM:</strong> Als Richtwert</li>
            <li><strong>Kleinere Gänge bei Gegenwind:</strong> Hohe RPM spart Kraft</li>
            <li><strong>Große Gänge nur bei Rückenwind/Abfahrt:</strong> Sonst Kniebelastung!</li>
          </ul>

          <h3>Gangwechsel-Strategie</h3>
          <div className="info-box">
            <h4>Kettenschaltung</h4>
            <ul>
              <li><strong>Kleine Sprünge:</strong> Hinten schalten (feine Anpassung)</li>
              <li><strong>Große Sprünge:</strong> Vorne schalten (grobe Anpassung)</li>
              <li><strong>Nicht kreuzen:</strong> Großes Blatt vorne + großes hinten = Verschleiß</li>
            </ul>
          </div>

          <h3>E-Bike Schaltung</h3>
          <ul>
            <li><strong>Auch mit Motor schalten!</strong> Nicht nur Turbo drücken</li>
            <li><strong>Motor entlasten beim Schalten:</strong> Kurz Druck rausnehmen</li>
            <li><strong>Optimale Trittfrequenz:</strong> 70-80 RPM für Motor-Effizienz</li>
          </ul>
        </section>

        <section>
          <h2>6. Besondere Situationen</h2>
          
          <h3>Cattle Grids (Viehgitter)</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Cattle Grids sind rutschig!</strong> Besonders bei Nässe können schmale 
            Reifen zwischen die Stäbe rutschen.
          </div>
          <ul>
            <li><strong>Geschwindigkeit reduzieren</strong></li>
            <li><strong>Gerade Linie</strong> (nicht schräg!)</li>
            <li><strong>Gewicht zentral</strong></li>
            <li><strong>Alternativ:</strong> Seitliche Pforte benutzen (falls vorhanden)</li>
          </ul>

          <h3>Fords (Wasserdurchfahrten)</h3>
          <ul>
            <li><strong>Tiefe prüfen:</strong> Erst schauen, dann fahren</li>
            <li><strong>Langsam durchfahren:</strong> Spritzwasser vermeiden</li>
            <li><strong>Nach Durchfahrt:</strong> Bremsen mehrmals testen!</li>
            <li><strong>Bei Regen meiden:</strong> Wasser kann reißend sein</li>
          </ul>

          <h3>Schafe auf der Straße</h3>
          <ul>
            <li><strong>Langsam fahren</strong></li>
            <li><strong>Nicht klingeln/hupen</strong> (erschreckt Tiere)</li>
            <li><strong>Platz geben</strong> (Schafe sind unberechenbar)</li>
            <li><strong>Geduld!</strong> Highland-Schafe kennen keine Eile</li>
          </ul>
        </section>

        <section>
          <h2>7. Pacing & Energie-Management</h2>
          
          <h3>Lange Tagesetappen (80-120km)</h3>
          <ul>
            <li><strong>Erste Stunde locker:</strong> 60-70% Leistung zum Warmwerden</li>
            <li><strong>Gleichmäßiges Tempo:</strong> Nicht zu viele Antritte</li>
            <li><strong>Essen alle 1-1.5h:</strong> Auch wenn du keinen Hunger hast</li>
            <li><strong>Trinken alle 15-20 Min:</strong> Kleine Schlucke</li>
            <li><strong>Letzte 20km:</strong> Reserve für unerwartete Anstiege</li>
          </ul>

          <h3>Ernährung unterwegs</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>Vor der Tour:</strong> Kohlenhydrate (Porridge, Brot)
            </div>
            <div className="data-item">
              <strong>Während (pro Stunde):</strong> 30-60g Kohlenhydrate
            </div>
            <div className="data-item">
              <strong>Nach der Tour:</strong> Proteine & KH (Recovery)
            </div>
            <div className="data-item">
              <strong>Trinken:</strong> 500-750ml pro Stunde
            </div>
          </div>

          <h3>Pausen richtig machen</h3>
          <ul>
            <li><strong>Kurze Pausen (5-10 Min.):</strong> Alle 30-40km</li>
            <li><strong>Lange Pause (30-60 Min.):</strong> Einmal am Tag (Mittagessen)</li>
            <li><strong>Nicht zu lange:</strong> Muskeln kühlen aus, schwerer wieder loszufahren</li>
            <li><strong>Stretching:</strong> Bei langen Pausen Beine lockern</li>
          </ul>
        </section>

        <div className="pro-tip">
          <h3>🎯 Die 5 wichtigsten Fahrtechnik-Tipps für Schottland</h3>
          <ol>
            <li><strong>Rechtzeitig schalten:</strong> Vor Anstiegen runter, vor Abfahrten hoch</li>
            <li><strong>Bremsen bei Nässe:</strong> Früher & sanfter, mehr hinten als vorne</li>
            <li><strong>Wind respektieren:</strong> Tempo anpassen, bei Sturm pausieren</li>
            <li><strong>Pace dich:</strong> Schottland ist hügelig - lange Anstiege langsam angehen</li>
            <li><strong>Kommunikation:</strong> Handzeichen & Augenkontakt (vor allem im Linksverkehr!)</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/sicherheit">🦺 Sicherheits-Guide</Link> - Notfallplanung & Erste Hilfe</li>
            <li><Link to="/wissen/verkehrsregeln">🚦 Verkehrsregeln UK</Link> - Linksverkehr & Highway Code</li>
            <li><Link to="/wissen/ebike">⚡ E-Bike Basics</Link> - Motor & Akku optimal nutzen</li>
          </ul>
        </div>
      </article>
    </div>
  );
}
